import { Question, OASISSchema, OASISFormData, FormValue } from './types';

/**
 * Initializes default values for pseudo questions.
 * Sets pseudo questions with "Yes" option to "Yes" by default.
 *
 * @param formData The current form data
 * @param schema The OASIS schema containing all questions
 * @returns Form data with pseudo question defaults applied
 */
export const initializePseudoQuestionDefaults = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = { ...formData };

  const processQuestion = (question: Question) => {
    // If this is a pseudo question with "Yes" option, default to "Yes"
    if (
      question.is_pseudo === true &&
      question.subitems?.['Yes'] &&
      result[question.qid] === undefined
    ) {
      result[question.qid] = 'Yes';
    }

    // Process all subitems recursively
    if (question.subitems) {
      Object.values(question.subitems).forEach(subitems => {
        subitems.forEach(processQuestion);
      });
    }
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(processQuestion);
  });

  return result;
};

/**
 * Checks if a value is meaningful (not empty).
 */
const hasValue = (value: FormValue): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
};

const getSelectedSubitems = (
  question: Question,
  value: FormValue
): Question[] => {
  if (!question.subitems) return [];

  const selectedValues =
    typeof value === 'string'
      ? [value]
      : Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : [];

  const seenQids = new Set<string>();
  const selectedSubitems: Question[] = [];

  selectedValues.forEach(selectedValue => {
    (question.subitems?.[selectedValue] ?? []).forEach(subitem => {
      if (seenQids.has(subitem.qid)) return;
      seenQids.add(subitem.qid);
      selectedSubitems.push(subitem);
    });
  });

  return selectedSubitems;
};

/**
 * Checks if any subitem (recursively) has a meaningful value in the form data.
 */
const hasSubitemData = (
  subitems: Question[],
  formData: OASISFormData
): boolean => {
  for (const subitem of subitems) {
    const value = formData[subitem.qid];

    // If this subitem has a meaningful value (and is not a pseudo question), it has data
    if (hasValue(value) && subitem.is_pseudo !== true) {
      return true;
    }

    // Check nested subitems recursively
    if (subitem.subitems) {
      const activeNestedSubitems = getSelectedSubitems(subitem, value);
      if (
        activeNestedSubitems.length > 0 &&
        hasSubitemData(activeNestedSubitems, formData)
      ) {
        return true;
      }
    }
  }
  return false;
};

interface GetOASISFormDataOptions {
  /**
   * When true, include visible but unanswered questions as "" in the output.
   * Use for validation calls so the backend can detect mandatory unanswered questions.
   * Do NOT use for save/persistence — it would store empty strings for every untouched question.
   */
  includeVisibleEmpty?: boolean;
}

/**
 * Extracts OASIS form data for persistence or validation.
 * Returns data in QID format (the same format used in the UI).
 * Pseudo questions are only included if their subitems have actual data
 * (or if includeVisibleEmpty is set, since subitems under "Yes" are always visible).
 *
 * @param formData The complete form data including pseudo questions
 * @param schema The OASIS schema containing all questions
 * @param options Optional settings (e.g. includeVisibleEmpty for validation)
 * @returns Form data with QIDs as keys
 */
export const getOASISFormData = (
  formData: OASISFormData,
  schema: OASISSchema,
  options?: GetOASISFormDataOptions
): OASISFormData => {
  const includeVisibleEmpty = options?.includeVisibleEmpty ?? false;
  const result: OASISFormData = {};

  // Process all questions recursively
  const processQuestion = (question: Question) => {
    const value = formData[question.qid];

    // For pseudo questions with "Yes" option, only include if subitems have data
    // (or always when includeVisibleEmpty, since pseudo defaults to "Yes" and subitems are visible)
    if (question.is_pseudo === true && question.subitems?.['Yes']) {
      const subitems = question.subitems['Yes'];
      if (hasSubitemData(subitems, formData) || includeVisibleEmpty) {
        result[question.qid] = 'Yes';
        // Process subitems
        subitems.forEach(subitem => processQuestion(subitem));
      }
      return;
    }

    // Include non-pseudo question value if it has a meaningful value
    if (hasValue(value)) {
      result[question.qid] = value;
    } else if (includeVisibleEmpty && question.is_pseudo !== true) {
      // Visible but unanswered — send "" so backend can detect mandatory gaps
      result[question.qid] = '';
    }

    // Process subitems for the currently selected value
    if (question.subitems) {
      getSelectedSubitems(question, value).forEach(subitem =>
        processQuestion(subitem)
      );
    }
  };

  // Process all categories
  Object.values(schema).forEach(questions => {
    questions.forEach(question => processQuestion(question));
  });

  return result;
};

/**
 * Clears subitem data when a parent question's value changes.
 * Returns an object with subitem keys to be cleared (set to undefined).
 *
 * @param question The parent question that changed
 * @param newValue The new value selected for the parent question
 * @param currentFormData Current form data to check for existing subitem values
 * @returns Object with subitem keys that should be cleared
 */
export const getSubitemClearUpdates = (
  question: Question,
  newValue: FormValue,
  currentFormData: OASISFormData
): Partial<OASISFormData> => {
  const clearUpdates: Partial<OASISFormData> = {};

  if (!question.subitems) {
    return clearUpdates;
  }

  // Collect all subitem QIDs recursively
  const collectAllQids = (q: Question, qids: Set<string>) => {
    if (q.subitems) {
      Object.values(q.subitems).forEach(items => {
        items.forEach(item => {
          qids.add(item.qid);
          collectAllQids(item, qids);
        });
      });
    }
  };

  // Get all possible subitem QIDs
  const allQids = new Set<string>();
  collectAllQids(question, allQids);

  // Get QIDs that should remain active (for the new value)
  const activeQids = new Set<string>();
  getSelectedSubitems(question, newValue).forEach(subitem => {
    activeQids.add(subitem.qid);
    collectAllQids(subitem, activeQids);
  });

  // Clear inactive subitems that have values
  allQids.forEach(qid => {
    if (!activeQids.has(qid) && currentFormData[qid] !== undefined) {
      clearUpdates[qid] = undefined;
    }
  });

  return clearUpdates;
};
